// BDSJEA-366/371 (Historial, incremento 2): preparacion PURA (sin React) de las
// filas y opciones de filtro de cada tabla del historial, y de los datos de cada
// grafico. DashboardMigracion solo compone estos resultados.
//
// Tolerancia: las corridas viejas no traen id_cliente / cuit_productor /
// observaciones / id_entidad; todo se resuelve a "vacio" (la tabla muestra "-").
import { VALOR_SIN_DATO } from "./TablaImportacion"
import { motivoVisible } from "./motivoVisible"
import {
  categorizarArca,
  categorizarProcesados,
  observacionesCodigos,
  OBSERVACIONES_CERRADAS,
} from "./categorizacionHistorial"
import {
  clasificarResultado,
  etiquetaResultadoFila,
  normalizarId,
  reemplazarEntidadPorCodigo,
} from "./migracionEstadoHelpers"

const vacio = valor => valor == null || String(valor).trim() === ""

// Entidad de una fila: codigo del catalogo, o "Entidad N" si no se conoce.
export function codigoEntidad(idEntidad, mapaEntidades) {
  const id = normalizarId(idEntidad)
  if (id == null) return "-"
  return mapaEntidades?.[id] || `Entidad ${id}`
}

// Fila con resultado legible, motivo SANEADO (sin nombres, host/URL ni CUIT; con
// el codigo de entidad) y entidad como codigo. Tolera historiales viejos sin
// id_entidad / motivo.
const prepararFila = (f, mapaEntidades, contexto) => ({
  ...f,
  resultado_etiqueta: etiquetaResultadoFila(f.resultado),
  motivo: motivoVisible(
    reemplazarEntidadPorCodigo(f.motivo, mapaEntidades),
    contexto,
    mapaEntidades ? Object.values(mapaEntidades) : [],
  ),
  entidad: codigoEntidad(f.id_entidad, mapaEntidades),
})

export const prepararProcesadosProductores = (filas, mapaEntidades) =>
  filas.map(f => prepararFila(f, mapaEntidades, "productores"))

export const prepararNoImportadas = (filas, mapaEntidades) =>
  filas.map(f => prepararFila(f, mapaEntidades, "productores"))

// ARCA: el motivo es un texto fijo que normaliza el backend; con un backend
// viejo llega el texto crudo (host/URL), que se sanea igual.
export const prepararProcesadosArca = (filas, mapaEntidades) =>
  filas.map(f => ({
    ...f,
    motivo: motivoVisible(f.motivo, "arca"),
    resultado_etiqueta: etiquetaResultadoFila(f.resultado),
    entidad: codigoEntidad(f.id_entidad, mapaEntidades),
  }))

// Map id_productor (normalizado) -> cuit_original, desde el CSV de productores.
// Sirve de respaldo cuando una fila de establecimientos no trae cuit_productor
// (corridas viejas).
export function cuitPorProductor(productores) {
  const mapa = new Map()
  ;(Array.isArray(productores) ? productores : []).forEach(p => {
    const id = normalizarId(p?.id_productor)
    if (id != null && !vacio(p?.cuit_original) && !mapa.has(id)) {
      mapa.set(id, p.cuit_original)
    }
  })
  return mapa
}

const conCuit = (fila, mapaCuit) => {
  if (!vacio(fila.cuit_productor)) return fila
  const id = normalizarId(fila.id_productor)
  const cuit = id == null ? undefined : mapaCuit.get(id)
  return cuit ? { ...fila, cuit_productor: cuit } : fila
}

export const prepararCreadosEstablecimientos = (
  filas,
  mapaCuit,
  mapaEntidades,
) =>
  filas.map(f => ({
    ...conCuit(f, mapaCuit),
    entidad: codigoEntidad(f.id_entidad, mapaEntidades),
    observaciones_codigos: observacionesCodigos(f.observaciones),
  }))

export const prepararFallidosEstablecimientos = (
  filas,
  mapaEntidades,
  mapaCuit,
) =>
  filas.map(f =>
    conCuit(prepararFila(f, mapaEntidades, "establecimientos"), mapaCuit),
  )

// ----- Opciones de filtro: [{ value, label }] segun lo que hay en las filas.

export function opcionesEntidadFiltro(filas, mapaEntidades) {
  const ids = new Set()
  let sinEntidad = false
  filas.forEach(f => {
    const id = normalizarId(f.id_entidad)
    if (id == null) sinEntidad = true
    else ids.add(id)
  })
  const opciones = [...ids]
    .sort((a, b) => a - b)
    .map(id => ({
      value: String(id),
      label: codigoEntidad(id, mapaEntidades),
    }))
  if (sinEntidad) opciones.push({ value: VALOR_SIN_DATO, label: "Sin entidad" })
  return opciones
}

export function opcionesResultadoFiltro(filas) {
  const valores = new Set()
  let sinResultado = false
  filas.forEach(f => {
    if (vacio(f.resultado)) sinResultado = true
    else valores.add(String(f.resultado))
  })
  const opciones = [...valores]
    .map(v => ({ value: v, label: etiquetaResultadoFila(v) }))
    .sort((a, b) => a.label.localeCompare(b.label, "es"))
  if (sinResultado)
    opciones.push({ value: VALOR_SIN_DATO, label: "Sin resultado" })
  return opciones
}

export const OPCIONES_OBSERVACIONES = [
  ...OBSERVACIONES_CERRADAS.map(o => ({ value: o.codigo, label: o.etiqueta })),
  { value: VALOR_SIN_DATO, label: "Sin observaciones" },
]

// Pertenencia: la fila tiene ese codigo; VALOR_SIN_DATO => no tiene ninguno.
export const coincideObservacion = (fila, valor) => {
  const codigos = fila.observaciones_codigos || []
  return valor === VALOR_SIN_DATO
    ? codigos.length === 0
    : codigos.includes(valor)
}

// ----- Datos de los graficos (todo categorizado: nunca texto libre).

// Productores: Creados / Actualizados / Ya existian + motivos cerrados top-N.
export function categoriasProductores(productores, topN = 4) {
  const r = categorizarProcesados(productores, {
    contexto: "productores",
    topN,
  })
  return [
    { etiqueta: "Creados", cantidad: r.creados, fijo: true, color: "#28a745" },
    {
      etiqueta: "Actualizados",
      cantidad: r.actualizados,
      fijo: true,
      color: "#007bff",
    },
    {
      etiqueta: "Ya existían",
      cantidad: r.yaExistian,
      fijo: true,
      color: "#20c997",
    },
    ...r.motivos,
  ].filter(c => c.cantidad > 0)
}

// Establecimientos: total PROCESADO = creados + (actualizados / ya existian,
// que NO son fallo) + no importados por motivo (top-N + "Otros motivos").
export function datosGraficoEstablecimientos(establecimientos, topN = 4) {
  const r = categorizarProcesados(establecimientos, {
    contexto: "establecimientos",
    topN,
  })
  return {
    total: r.total,
    creados: r.creados,
    noImportados: r.noImportados,
    motivos: [
      { etiqueta: "Actualizados", cantidad: r.actualizados },
      { etiqueta: "Ya existía", cantidad: r.yaExistian },
      ...r.motivos,
    ].filter(m => m.cantidad > 0),
  }
}

// ARCA: SINCRONIZADO / SIN_DATOS / otros motivos. SIN_DATOS ya tiene su propio
// segmento: se saca de la lista de motivos para no contarlo dos veces.
export function datosGraficoArca(arca, topN = 4) {
  const r = categorizarArca(arca, { topN })
  return {
    total: r.total,
    sincronizados: r.sincronizados,
    sinDatos: r.sinDatos,
    motivos: r.motivos.filter(m => m.clave !== "SIN_DATOS"),
  }
}

export const esNoImportado = (f, tipo) =>
  clasificarResultado(f.resultado, tipo).noImportado
